import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { XIcon, LoaderIcon } from 'lucide-react';
type CalendlyModalProps = {
  open: boolean;
  onClose: () => void;
  /** Lädt den Kalender im Hintergrund vor, damit er beim Klick sofort da ist. */
  preload?: boolean;
};

/**
 * Terminbuchung als eigenes Modal mit eingebettetem Calendly-Kalender.
 * Farben sind an die Website angepasst (Creme, dunkler Text, Hot-Pink).
 */
const CALENDLY_URL = 'https://calendly.com/tinaxla/erstgesprach' + '?hide_gdpr_banner=1' + '&hide_landing_page_details=1' + '&background_color=fdf9f6' + '&text_color=262626' + '&primary_color=e93b8f';
export function CalendlyModal({
  open,
  onClose,
  preload = false
}: CalendlyModalProps) {
  const [mounted, setMounted] = useState(false);
  const [loaded, setLoaded] = useState(false);
  // Zählt hoch, sobald das Modal geschlossen wird – dadurch startet der
  // Kalender beim nächsten Öffnen wieder beim ersten Schritt.
  const [frameKey, setFrameKey] = useState(0);
  const wasOpen = useRef(false);
  useEffect(() => {
    if (wasOpen.current && !open) {
      setLoaded(false);
      setFrameKey(key => key + 1);
    }
    wasOpen.current = open;
  }, [open]);

  // Verbindung zu Calendly früh aufbauen, damit nichts auf den Handshake wartet.
  useEffect(() => {
    const links = ['https://calendly.com', 'https://assets.calendly.com'].flatMap(href => (['preconnect', 'dns-prefetch'] as const).map(rel => {
      const link = document.createElement('link');
      link.rel = rel;
      link.href = href;
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
      return link;
    }));
    return () => links.forEach(link => link.remove());
  }, []);

  // Sobald vorgeladen oder geöffnet wird, bleibt der Kalender im DOM.
  useEffect(() => {
    if (open || preload) setMounted(true);
  }, [open, preload]);
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);
  if (!mounted) return null;

  // Über ein Portal direkt am <body>, damit das Overlay immer über der
  // gesamten Seite liegt – unabhängig vom umgebenden Abschnitt.
  return createPortal(<div aria-hidden={!open} onClick={open ? onClose : undefined} className={`fixed inset-0 z-[70] flex items-center justify-center p-2 transition-opacity duration-150 ease-out sm:p-4 ${open ? 'bg-neutral-900/60 opacity-100 backdrop-blur-sm' : 'pointer-events-none opacity-0'}`} data-id="element-191" data-mp="1qh2jd7sx3hnl">
      <motion.div role="dialog" aria-modal="true" aria-label="Kostenloses Gespräch buchen" onClick={e => e.stopPropagation()} initial={false} animate={open ? {
      opacity: 1,
      scale: 1,
      y: 0
    } : {
      opacity: 0,
      scale: 0.94,
      y: 12
    }} transition={{
      duration: 0.22,
      ease: 'easeOut'
    }} className="relative h-[94vh] max-h-[94vh] w-full max-w-6xl overflow-hidden rounded-2xl bg-cream shadow-2xl sm:h-[92vh] md:max-lg:h-[624px] md:max-lg:w-[728px] md:max-lg:max-w-[728px] lg:max-xl:h-[790px] lg:max-xl:w-[986px] lg:max-xl:max-w-[986px]" data-id="element-192" data-mp="5ofgwpc4vox">
        <button type="button" onPointerUp={e => {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }} onClick={e => {
        e.stopPropagation();
        onClose();
      }} aria-label="Schließen" className="absolute right-3 top-3 z-20 flex h-11 w-11 touch-manipulation items-center justify-center rounded-full bg-white text-neutral-700 shadow-md transition-colors duration-150 ease-out hover:bg-white hover:text-neutral-900" data-id="element-193" data-mp="6jbu6x5kc0d2">
          <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-194" data-mp="et98wqzp20db" />
        </button>

        {/* Der Calendly-Rahmen bringt eigenen Innenabstand mit – wir schneiden
            ihn weg, damit kein leerer Creme-Rand um den Kalender bleibt. */}
        <div className="h-full w-full overflow-hidden" data-id="element-195" data-mp="53pmxcgopqe8">
          <iframe key={frameKey} src={CALENDLY_URL} title="Terminbuchung – kostenloses Erstgespräch" className={`calendly-frame border-0 transition-opacity duration-150 ease-out ${loaded ? 'opacity-100' : 'opacity-0'}`} onLoad={() => setLoaded(true)} style={{
          width: 'calc(100% + 96px)',
          height: 'calc(100% + 96px)',
          marginLeft: '-48px',
          marginTop: '-48px'
        }} data-id="element-196" data-mp="ss8zw5aoircn" />
        </div>

        {!loaded && <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-cream" data-id="element-197" data-mp="310dq1yttbbqo">
            <LoaderIcon className="h-6 w-6 animate-spin text-gold" aria-hidden="true" data-id="element-198" data-mp="2wz29sjwtw2v5" />
            <p className="text-sm text-neutral-600" data-id="element-199" data-mp="3ayjrkfbpifv0">Kalender wird geladen…</p>
          </div>}

        {/* Gleicher Creme-Glitter wie im restlichen Website-Hintergrund */}
        <span aria-hidden="true" className="gold-glitter-layer-static hidden sm:block" style={{
        zIndex: 10
      }} data-id="element-200" data-mp="35xdwwbyhkiiu" />
        <span aria-hidden="true" className="gold-glitter-extra hidden sm:block" style={{
        zIndex: 10
      }} data-id="element-201" data-mp="2dbktc05d8zv0" />
      </motion.div>
    </div>, document.body);
}